import {
  Badge,
  Button,
  Caption1,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  Alert24Regular,
  ArrowRight24Regular,
  Chat24Regular,
  Dismiss24Regular,
  Navigation24Regular,
  Person24Regular,
  Search24Regular,
  Settings24Regular,
  SignOut24Regular,
  Trophy24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
} from '@fluentui/react-icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { messagesApi, notificationsApi } from '@/api/endpoints'
import { UserAvatar } from '@/components/common/ui'
import { fmtRelative } from '@/components/common/dateTime'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { isAdmin } from '@/utils/auth'

const useStyles = makeStyles({
  bar: {
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0 16px',
    position: 'sticky',
    top: '0px',
    zIndex: 100,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  search: {
    flex: 1,
    maxWidth: '440px',
    minWidth: '120px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },
  iconBtn: {
    minWidth: '0px',
    width: '38px',
    height: '38px',
    padding: '0px',
  },
  notifItem: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  notifUnread: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': { backgroundColor: tokens.colorBrandBackground2Hover },
  },
})

export function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const s = useStyles()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { user, logout } = useAuthStore()
  const { theme, toggleTheme, unreadNotifications, setUnreadNotifications, unreadMessages, setUnreadMessages } = useUiStore()
  const [q, setQ] = useState('')
  const [notifOpen, setNotifOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // 未读通知
  const { data: notifications } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: notificationsApi.list,
    enabled: !!user,
    refetchInterval: 60_000,
  })
  // 未读私信（来自会话汇总）
  const { data: conversations } = useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: messagesApi.conversations,
    enabled: !!user,
    refetchInterval: 60_000,
  })

  const unread = notifications?.filter((n) => !n.isRead).length ?? 0
  const msgUnread = conversations?.reduce((acc, c) => acc + (c.unreadCount || 0), 0) ?? 0
  useEffect(() => {
    if (unread !== unreadNotifications) setUnreadNotifications(unread)
  }, [unread, unreadNotifications, setUnreadNotifications])
  useEffect(() => {
    if (msgUnread !== unreadMessages) setUnreadMessages(msgUnread)
  }, [msgUnread, unreadMessages, setUnreadMessages])

  const doSearch = () => {
    const v = q.trim()
    nav(v ? `/search?q=${encodeURIComponent(v)}` : '/search')
    setQ('')
  }

  const handleLogout = () => {
    logout()
    qc.clear()
    nav('/')
  }

  return (
    <header className={s.bar}>
      <Button className={s.iconBtn} appearance="subtle" icon={<Navigation24Regular />} onClick={onMenuClick} aria-label="打开导航" />

      <div className={s.search}>
        <Input
          ref={searchRef}
          value={q}
          onChange={(_, d) => setQ(d.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          placeholder="搜索赛事 / 战队 / 资讯…"
          size="large"
          appearance="filled-darker"
          contentBefore={<Search24Regular />}
          aria-label="全站搜索"
        />
      </div>

      <div className={s.right}>
        {/* 主题切换 */}
        <Tooltip content={theme === 'light' ? '切换到深色主题' : '切换到浅色主题'} relationship="label">
          <Button className={s.iconBtn} appearance="subtle" onClick={toggleTheme} aria-label="切换主题">
            {theme === 'light' ? <WeatherMoon24Regular /> : <WeatherSunny24Regular />}
          </Button>
        </Tooltip>

        {user && (
          <>
            {/* 消息中心 */}
            <Tooltip content="消息中心" relationship="label">
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <Button
                  className={s.iconBtn}
                  appearance="subtle"
                  onClick={() => nav('/messages')}
                  aria-label="消息中心"
                  icon={<Chat24Regular />}
                />
                {msgUnread > 0 && (
                  <Badge
                    appearance="filled"
                    color="danger"
                    style={{ position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, borderRadius: 8, fontSize: 10, padding: 0, justifyContent: 'center' }}
                  >
                    {msgUnread > 99 ? '99+' : msgUnread}
                  </Badge>
                )}
              </span>
            </Tooltip>

            {/* 通知中心 */}
            <Popover
              open={notifOpen}
              onOpenChange={(_, d) => setNotifOpen(d.open)}
              positioning={{ position: 'below', align: 'end' }}
            >
              <PopoverTrigger disableButtonEnhancement>
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <Button className={s.iconBtn} appearance="subtle" aria-label="通知中心" icon={<Alert24Regular />} />
                  {unread > 0 && (
                    <Badge
                      appearance="filled"
                      color="danger"
                      style={{ position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, borderRadius: 8, fontSize: 10, padding: 0, justifyContent: 'center' }}
                    >
                      {unread > 99 ? '99+' : unread}
                    </Badge>
                  )}
                </span>
              </PopoverTrigger>
              <PopoverSurface style={{ width: '340px', maxHeight: '480px', overflowY: 'auto', padding: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px 8px' }}>
                  <Text weight="semibold">通知</Text>
                  {unread > 0 && (
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => {
                        notificationsApi.readAll().then(() => {
                          qc.invalidateQueries({ queryKey: ['notifications'] })
                        })
                      }}
                    >
                      全部已读
                    </Button>
                  )}
                </div>
                {(notifications ?? []).length === 0 && (
                  <Caption1 style={{ padding: '16px 8px', display: 'block', color: tokens.colorNeutralForeground3 }}>
                    暂无通知
                  </Caption1>
                )}
                {(notifications ?? [])
                  .slice(0, 20)
                  .map((n) => (
                    <div
                      key={n.id}
                      className={`${s.notifItem}${n.isRead ? '' : ` ${s.notifUnread}`}`}
                      onClick={() => {
                        if (!n.isRead) {
                          notificationsApi.read(n.id).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))
                        }
                      }}
                    >
                      <Text size={400} style={{ marginTop: '1px' }}>
                        {notifIcon(n.type)}
                      </Text>
                      <div style={{ flex: 1, minWidth: '0px' }}>
                        <Text block size={200} style={{ whiteSpace: 'normal' }}>
                          {notifText(n)}
                        </Text>
                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{fmtRelative(n.createdAt)}</Caption1>
                      </div>
                    </div>
                  ))}
              </PopoverSurface>
            </Popover>

            {/* 用户菜单 */}
            <Menu positioning={{ position: 'below', align: 'end' }}>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" style={{ padding: '4px 8px', height: '40px' }} aria-label="用户菜单">
                  <UserAvatar src={user.avatarUrl} name={user.fullName} size={30} />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <div style={{ padding: '8px 12px 4px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, marginBottom: '4px' }}>
                    <Text block weight="semibold">
                      {user.fullName}
                    </Text>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{user.email}</Caption1>
                  </div>
                  <MenuItem icon={<Person24Regular />} onClick={() => nav('/profile')}>
                    个人中心
                  </MenuItem>
                  <MenuItem icon={<Trophy24Regular />} onClick={() => nav('/my/events')}>
                    我的赛事
                  </MenuItem>
                  <MenuItem icon={<Settings24Regular />} onClick={() => nav('/profile/settings')}>
                    设置
                  </MenuItem>
                  {isAdmin(user) && (
                    <MenuItem icon={<ArrowRight24Regular />} onClick={() => nav('/admin')}>
                      管理后台
                    </MenuItem>
                  )}
                  <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                    退出登录
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        )}

        {!user && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button appearance="secondary" onClick={() => nav('/login')}>
              登录
            </Button>
            <Button appearance="primary" onClick={() => nav('/register')}>
              注册
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}

/** 通知图标与文案 */
function notifIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'achievement':
      return '🏆'
    case 'match':
      return '⚔️'
    case 'message':
      return '💬'
    case 'registration':
      return '📋'
    case 'system':
      return '🔔'
    default:
      return '🔔'
  }
}

function notifText(n: { type: string; payload: string }) {
  try {
    const p = JSON.parse(n.payload || '{}') as { title?: string; message?: string; content?: string }
    return p.title || p.message || p.content || n.type
  } catch {
    return n.payload || n.type
  }
}
